import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import type { SiteData } from "../shared/types";
import { DOCS_BASE } from "../nav";
import { SEARCH_BUNDLE } from "../shared/paths";
import { COPY } from "../copy";

/**
 * Site search (design §0.2: ⌘K, one index for the whole site, tab filters) on Pagefind's index of
 * the prerendered pages (`npm run docs:build`). The index exists only in a built site; the dev
 * server says so instead of failing.
 */

interface PagefindResult {
  url: string;
  excerpt: string;
  meta: { title?: string };
}
interface Pagefind {
  init: () => Promise<void>;
  search: (query: string, options?: { filters?: Record<string, string> }) => Promise<{ results: { data: () => Promise<PagefindResult> }[] }>;
}

let pagefindPromise: Promise<Pagefind | null> | null = null;
const loadPagefind = (): Promise<Pagefind | null> => {
  if (!pagefindPromise) {
    pagefindPromise = import(/* webpackIgnore: true */ SEARCH_BUNDLE)
      .then(async (mod: Pagefind) => {
        await mod.init();

        return mod;
      })
      .catch(() => null);
  }

  return pagefindPromise;
};

/** Pagefind indexes `/docs/x/index.html`; the site's URL has no `index.html`. */
const pageUrl = (url: string) => url.replace(/\/index\.html$/, "").replace(/\.html$/, "") || DOCS_BASE;

export const SearchDialog = ({ site, open, onClose }: { site: SiteData; open: boolean; onClose: () => void }) => {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<string | null>(null);
  const [results, setResults] = useState<PagefindResult[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || !query.trim()) {
      setResults([]);

      return undefined;
    }
    let current = true;
    loadPagefind().then(async (pagefind) => {
      if (!pagefind) {
        if (current) setUnavailable(true);

        return;
      }
      const found = await pagefind.search(query, tab ? { filters: { tab } } : undefined);
      const data = await Promise.all(found.results.slice(0, 10).map((r) => r.data()));
      if (current) setResults(data);
    });

    return () => {
      current = false;
    };
  }, [open, query, tab]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-trueBlack/50 p-4 pt-24" role="dialog" aria-modal="true" aria-label={COPY.search} onMouseDown={onClose}>
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-popover shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-4">
          <Search size={16} className="text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && onClose()}
            placeholder={COPY.searchPlaceholder}
            className="h-12 flex-1 bg-transparent text-bodyMedium text-foreground outline-none"
          />
          <button type="button" onClick={onClose} className="text-muted-foreground" aria-label={COPY.closeNavigation}>
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-wrap gap-1 border-b border-border px-4 py-2">
          {[{ id: null, label: COPY.allTags }, ...site.tabs.map((t) => ({ id: t.label, label: t.label }))].map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => setTab(option.id)}
              className={clsx("rounded-full px-2.5 py-1 text-headline2", tab === option.id ? "bg-muted text-foreground" : "text-muted-foreground")}
            >
              {option.label}
            </button>
          ))}
        </div>
        <ul className="max-h-96 overflow-y-auto p-2">
          {unavailable ? <li className="px-2 py-3 text-bodySmall text-muted-foreground">{COPY.searchUnavailable}</li> : null}
          {!unavailable && query.trim() && results.length === 0 ? <li className="px-2 py-3 text-bodySmall text-muted-foreground">{COPY.searchNoResults}</li> : null}
          {results.map((r) => (
            <li key={r.url}>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate(pageUrl(r.url));
                }}
                className="w-full rounded-lg px-3 py-2 text-left hover:bg-muted"
              >
                <span className="block font-semibold text-foreground">{r.meta.title}</span>
                <span className="block text-bodySmall text-muted-foreground [&_mark]:bg-primary [&_mark]:text-primary-foreground" dangerouslySetInnerHTML={{ __html: r.excerpt }} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
